import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const industries = [
    { slug: "government", name: "Government & Public Sector", short: "GOV", desc: "Digitize stamping, tendering, and contract governance across ministries.", products: ["Colecle eStamp", "Procurement & Contracts", "Colecle Monitor"] },
    { slug: "banking", name: "Banking & Financial Services", short: "FIN", desc: "Audit-grade document authentication, procurement governance and ICT observability.", products: ["Colecle eStamp", "Procurement & Contracts", "Colecle Monitor"] },
    { slug: "enterprise", name: "Corporate Enterprises", short: "ENT", desc: "One platform for contracts, procurement, IT operations, property and events.", products: ["Procurement & Contracts", "Colecle Monitor", "Colecle Rent", "Colecle Events"] },
    { slug: "sme", name: "SMEs", short: "SME", desc: "Simple, affordable tools to authenticate documents, track contracts and run events.", products: ["Colecle eStamp", "Procurement & Contracts", "Colecle Events"] },
    { slug: "property", name: "Property Management", short: "REI", desc: "Automate rent, leases, tenant communication and portfolio performance.", products: ["Colecle Rent", "Colecle eStamp"] },
    { slug: "ngo", name: "NGOs", short: "NGO", desc: "Transparent procurement, contract governance and impactful event coordination.", products: ["Procurement & Contracts", "Colecle Events", "Colecle eStamp"] },
    { slug: "events-orgs", name: "Event Organizers", short: "EVT", desc: "End-to-end event creation, registration, check-in and analytics.", products: ["Colecle Events"] },
];

export default function IndustrySolutions() {
    const [active, setActive] = useState(industries[0]);
    return (
        <section id="solutions" data-testid="home-industries" className="bg-white">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28 lg:py-36">
                <div className="grid lg:grid-cols-12 gap-8 mb-16">
                    <div className="lg:col-span-6">
                        <div className="eyebrow">Solutions by Industry</div>
                        <h2 className="headline text-4xl md:text-5xl lg:text-6xl mt-4 text-navy">Built for the way <br /> your industry operates.</h2>
                    </div>
                </div>

                <div className="grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5 space-y-1" data-testid="industry-tabs">
                        {industries.map((ind) => (
                            <button
                                key={ind.slug}
                                data-testid={`industry-tab-${ind.slug}`}
                                onMouseEnter={() => setActive(ind)}
                                onFocus={() => setActive(ind)}
                                onClick={() => setActive(ind)}
                                className={`w-full flex items-center justify-between text-left py-5 border-b transition ${
                                    active.slug === ind.slug ? "border-navy" : "border-slate-200 hover:border-slate-400"
                                }`}
                            >
                                <div className="flex items-center gap-5">
                                    <span className="font-mono text-[11px] text-slate-400 tracking-widest">{ind.short}</span>
                                    <span className={`font-serif text-xl md:text-2xl ${active.slug === ind.slug ? "text-navy" : "text-slate-500"}`}>{ind.name}</span>
                                </div>
                                <ArrowUpRight className={`w-4 h-4 transition ${active.slug === ind.slug ? "text-[#E8B923]" : "text-slate-300"}`} />
                            </button>
                        ))}
                    </div>
                    <div className="lg:col-span-7">
                        <div className="sticky top-24 bg-[#F7F9FC] border border-slate-200 rounded-2xl p-8 lg:p-10 min-h-[400px]">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={active.slug}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                >
                                    <div className="eyebrow eyebrow--slate">Industry Focus</div>
                                    <h3 className="font-serif text-3xl lg:text-4xl text-navy mt-4">{active.name}</h3>
                                    <p className="text-slate-600 text-base mt-4 leading-relaxed">{active.desc}</p>

                                    <div className="mt-8">
                                        <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3">Recommended products</div>
                                        <div className="flex flex-wrap gap-2">
                                            {active.products.map((p) => (
                                                <span key={p} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm text-navy">
                                                    {p}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    <Link to={`/solutions/${active.slug}`} data-testid={`industry-cta-${active.slug}`} className="btn-primary text-sm mt-8">
                                        Explore for {active.short} <ArrowUpRight className="w-4 h-4" />
                                    </Link>
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
